/**
 * Accounts payable — the screens for a module that has had none.
 *
 * Everything here already existed behind the API and had been tested for
 * weeks; none of it could be opened. What follows is the whole of it:
 * vendors, invoices, the weekly payment run with its checks, the
 * approval thresholds, and the four reports.
 *
 * ---------------------------------------------------------------------
 * THE RUN IS FOUR IRREVERSIBLE STEPS AND THE SCREEN SAYS SO
 *
 * A payment run goes select → number → print → post, and the order is
 * not decoration. Nothing touches the bank account until the checks
 * physically exist, because a club that posts first has a cash balance
 * that is wrong for as long as the printer is jammed. So the run screen
 * is a sequence with one live action at a time, and each says what it
 * will and will not do.
 *
 * ---------------------------------------------------------------------
 * TWO KINDS OF VOID, WHICH SHARE A WORD
 *
 * Spoiling a check consumes a number and produces no ledger entry: the
 * paper jammed, the number is gone, print the next one. Voiding a posted
 * check reverses an entry that has already hit the bank. They are
 * different operations and the screen never offers both at once.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, getToken } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, parseMoney, date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

// ---------------------------------------------------------------------

interface Vendor {
  id: string;
  vendor_number: string;
  name: string;
  legal_name: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  payment_terms_days: number;
  default_payment_method: string;
  is_1099_vendor: boolean;
  w9_on_file: boolean;
  form_1099_type: string | null;
  hold_payments: boolean;
  hold_reason: string | null;
  is_active: boolean;
  open_minor: string;
  open_invoices: number;
}

interface Invoice {
  ap_invoice_id: string;
  vendor_id: string;
  vendor_number: string;
  vendor_name: string;
  vendor_on_hold: boolean;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  description: string | null;
  status: string;
  hold: boolean;
  hold_reason: string | null;
  approval_status: string;
  total_minor: string;
  paid_minor: string;
  open_minor: string;
  days_overdue: number;
  discount_minor: string | null;
  discount_due_on: string | null;
  discount_available: boolean;
}

interface Run {
  id: string;
  run_number: string;
  method: string;
  status: string;
  payment_date: string;
  first_check_number: number | null;
  total_minor: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  note: string | null;
  created_at: string;
}

const STATUS_TONE: Record<string, 'good' | 'warning' | 'serious' | 'critical' | 'neutral'> = {
  paid: 'good',
  open: 'warning',
  partly_paid: 'warning',
  draft: 'neutral',
  void: 'critical',
  approved: 'good',
  pending: 'warning',
  rejected: 'critical',
  not_required: 'neutral',
  printed: 'warning',
  posted: 'good',
  cancelled: 'critical',
};

const tone = (s: string) => STATUS_TONE[s] ?? 'neutral';
const label = (s: string) => s.replace(/_/g, ' ');

// =====================================================================
// Vendors
// =====================================================================

export function VendorsPage() {
  const { currency } = useSession();
  const [q, setQ] = useState('');
  const [inactive, setInactive] = useState(false);
  const [only1099, setOnly1099] = useState(false);
  const [adding, setAdding] = useState(false);

  const query = new URLSearchParams();
  if (q.trim()) query.set('q', q.trim());
  if (inactive) query.set('include_inactive', 'true');
  if (only1099) query.set('only_1099', 'true');

  const { data, error, loading, reload } = useApi<{ vendors: Vendor[] }>(
    `/v1/ap/vendors?${query.toString()}`,
    [q, inactive, only1099],
  );

  const vendors = data?.vendors ?? [];
  const owed = vendors.reduce((n, v) => n + Number(v.open_minor), 0);

  return (
    <>
      <div className="row" style={{ marginBottom: 16, alignItems: 'baseline', gap: 12 }}>
        <h1>Vendors</h1>
        <span className="muted small">
          {vendors.length} · {money(owed, currency)} outstanding
        </span>
        <div className="spacer" />
        <button onClick={() => setAdding((a) => !a)}>
          {adding ? 'Cancel' : 'Add a vendor'}
        </button>
      </div>

      <ErrorBanner error={error} />

      {adding && (
        <VendorForm
          onDone={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      <Card
        title="Who the Club Buys From"
        actions={
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <label className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: 'auto', flex: '0 0 auto' }}
                checked={only1099}
                onChange={(e) => setOnly1099(e.target.checked)}
              />
              <span className="small">1099 only</span>
            </label>
            <label className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: 'auto', flex: '0 0 auto' }}
                checked={inactive}
                onChange={(e) => setInactive(e.target.checked)}
              />
              <span className="small">Show retired</span>
            </label>
          </div>
        }
      >
        <input
          placeholder="Search by name or vendor number"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        {loading ? (
          <Loading />
        ) : vendors.length === 0 ? (
          <Empty>No vendors match that.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Terms</th>
                <th>1099</th>
                <th className="num">Open</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}>
                  <td>
                    <Link to={`/ap/vendors/${v.id}`}>{v.name}</Link>{' '}
                    <span className="muted mono small">{v.vendor_number}</span>
                    {v.hold_payments && (
                      <div className="neg small">On hold — {v.hold_reason ?? 'no reason given'}</div>
                    )}
                    {!v.is_active && <div className="muted small">Retired</div>}
                  </td>
                  <td className="muted small">net {v.payment_terms_days}</td>
                  <td className="small">
                    {v.is_1099_vendor ? (
                      v.w9_on_file ? (
                        <Status tone="good">{v.form_1099_type ?? '1099'}</Status>
                      ) : (
                        <Status tone="critical">no W-9</Status>
                      )
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num">
                    {Number(v.open_minor) === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      money(v.open_minor, currency)
                    )}
                  </td>
                  <td className="muted small num">
                    {v.open_invoices > 0 && `${v.open_invoices} inv`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function VendorForm({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    contact_name: '',
    email: '',
    phone: '',
    payment_terms_days: '30',
    is_1099_vendor: false,
    w9_on_file: false,
  });

  const create = useMutation(async () =>
    api<{ vendor: Vendor; warning: string | null }>('/v1/ap/vendors', {
      method: 'POST',
      body: {
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || undefined,
        contact_name: form.contact_name.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        payment_terms_days: Number(form.payment_terms_days) || 30,
        is_1099_vendor: form.is_1099_vendor,
        w9_on_file: form.w9_on_file,
      },
    }),
  );

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card title="A New Vendor">
      <ErrorBanner error={create.error} />
      <div className="grid-2">
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={set('name')} placeholder="Rockford Linen Supply" />
          <div className="hint">The vendor number is assigned automatically.</div>
        </label>
        <label className="field">
          <span>Legal name, if different</span>
          <input value={form.legal_name} onChange={set('legal_name')} />
          <div className="hint">What goes on the 1099, when it differs from the trading name.</div>
        </label>
        <label className="field">
          <span>Contact</span>
          <input value={form.contact_name} onChange={set('contact_name')} />
        </label>
        <label className="field">
          <span>Email</span>
          <input value={form.email} onChange={set('email')} />
        </label>
        <label className="field">
          <span>Telephone</span>
          <input value={form.phone} onChange={set('phone')} />
        </label>
        <label className="field">
          <span>Payment terms, in days</span>
          <input
            type="number"
            value={form.payment_terms_days}
            onChange={set('payment_terms_days')}
            style={{ maxWidth: 120 }}
          />
          <div className="hint">The due date is worked out from this unless you set one.</div>
        </label>
      </div>

      <label className="row" style={{ gap: 8, marginBottom: 8 }}>
        <input
          type="checkbox"
          style={{ width: 'auto', flex: '0 0 auto' }}
          checked={form.is_1099_vendor}
          onChange={(e) => setForm((f) => ({ ...f, is_1099_vendor: e.target.checked }))}
        />
        <span>This vendor gets a 1099</span>
      </label>

      {form.is_1099_vendor && (
        <label className="row" style={{ gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', flex: '0 0 auto' }}
            checked={form.w9_on_file}
            onChange={(e) => setForm((f) => ({ ...f, w9_on_file: e.target.checked }))}
          />
          <span>
            Their W-9 is on file
            <span className="muted small">
              {' '}
              — chase it now rather than in January
            </span>
          </span>
        </label>
      )}

      <div className="actions" style={{ marginTop: 10 }}>
        <button
          disabled={!form.name.trim() || create.busy}
          onClick={async () => {
            const r = await create.run();
            if (r) onDone();
          }}
        >
          {create.busy ? 'Saving…' : 'Add the vendor'}
        </button>
      </div>
    </Card>
  );
}

export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency, atLeast } = useSession();
  const { data, error, loading, reload } = useApi<any>(`/v1/ap/vendors/${id}`);
  const [holdReason, setHoldReason] = useState('');

  const hold = useMutation(async (on: boolean) =>
    api(`/v1/ap/vendors/${id}/hold`, {
      method: 'POST',
      body: { hold: on, reason: on ? holdReason.trim() || undefined : undefined },
    }),
  );

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  const v: Vendor = data.vendor;

  return (
    <>
      <div className="row" style={{ marginBottom: 4, alignItems: 'baseline', gap: 10 }}>
        <h1>{v.name}</h1>
        <span className="muted mono">{v.vendor_number}</span>
        <div className="spacer" />
        <Link to="/ap/vendors">
          <button className="secondary small">All vendors</button>
        </Link>
      </div>

      {data.warning && <div className="notice">{data.warning}</div>}

      {v.hold_payments && (
        <div className="notice">
          <strong>Payments to this vendor are on hold.</strong>{' '}
          {v.hold_reason ?? 'No reason was recorded.'} Nothing will be selected for them in a
          payment run until the hold is lifted.
        </div>
      )}

      <div className="grid-2">
        <Card title="The Vendor">
          <dl className="pairs">
            <Pair k="Legal name" v={v.legal_name ?? '—'} />
            <Pair k="Contact" v={v.contact_name ?? '—'} />
            <Pair k="Email" v={v.email ?? '—'} />
            <Pair k="Telephone" v={v.phone ?? '—'} />
            <Pair k="Terms" v={`net ${v.payment_terms_days}`} />
            <Pair k="Pays by" v={label(v.default_payment_method)} />
            <Pair
              k="1099"
              v={v.is_1099_vendor ? `${v.form_1099_type ?? 'yes'}${v.w9_on_file ? '' : ' — no W-9'}` : 'no'}
            />
          </dl>

          {atLeast('controller') && (
            <div style={{ marginTop: 12 }}>
              {v.hold_payments ? (
                <button className="secondary small" onClick={() => void hold.run(false).then(reload)}>
                  Release the hold
                </button>
              ) : (
                <div className="row" style={{ gap: 8 }}>
                  <input
                    placeholder="Why hold payments?"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                  />
                  <button
                    className="secondary small"
                    disabled={!holdReason.trim()}
                    onClick={() => void hold.run(true).then(reload)}
                  >
                    Hold payments
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card title="What Is Owed">
          {data.aging ? (
            <table className="data">
              <tbody>
                <AgingRow k="Not yet due" v={data.aging.not_yet_due_minor} currency={currency} />
                <AgingRow k="1 – 30 days" v={data.aging.days_1_30_minor} currency={currency} />
                <AgingRow k="31 – 60 days" v={data.aging.days_31_60_minor} currency={currency} />
                <AgingRow k="61 – 90 days" v={data.aging.days_61_90_minor} currency={currency} />
                <AgingRow k="Over 90 days" v={data.aging.days_over_90_minor} currency={currency} />
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="num">
                    <strong>{money(data.aging.total_minor, currency)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <Empty>Nothing outstanding.</Empty>
          )}
        </Card>
      </div>

      <Card title="Invoices">
        {(data.invoices ?? []).length === 0 ? (
          <Empty>No invoices from this vendor yet.</Empty>
        ) : (
          <InvoiceTable invoices={data.invoices} currency={currency} hideVendor />
        )}
      </Card>

      <Card title="Payments">
        {(data.payments ?? []).length === 0 ? (
          <Empty>Nothing paid yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Check</th>
                <th>Status</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p: any) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.payment_date)}</td>
                  <td className="muted small">{label(p.method)}</td>
                  <td className="mono">{p.check_number ?? '—'}</td>
                  <td>
                    <Status tone={tone(p.status)}>{label(p.status)}</Status>
                  </td>
                  <td className="num">{money(p.amount_minor, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function Pair({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

function AgingRow({ k, v, currency }: { k: string; v: string | number; currency: any }) {
  return (
    <tr>
      <td className="muted">{k}</td>
      <td className="num">{Number(v) === 0 ? <span className="muted">—</span> : money(v, currency)}</td>
    </tr>
  );
}

// =====================================================================
// Invoices
// =====================================================================

function InvoiceTable({
  invoices,
  currency,
  hideVendor,
}: {
  invoices: Invoice[];
  currency: any;
  hideVendor?: boolean;
}) {
  return (
    <table className="data">
      <thead>
        <tr>
          {!hideVendor && <th>Vendor</th>}
          <th>Invoice</th>
          <th>Due</th>
          <th>Status</th>
          <th>Approval</th>
          <th className="num">Total</th>
          <th className="num">Open</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((i) => (
          <tr key={i.ap_invoice_id}>
            {!hideVendor && (
              <td>
                {i.vendor_name}
                {i.vendor_on_hold && <div className="neg small">vendor on hold</div>}
              </td>
            )}
            <td>
              <Link to={`/ap/invoices/${i.ap_invoice_id}`}>{i.invoice_number}</Link>
              {i.description && <div className="muted small">{i.description}</div>}
              {i.hold && <div className="neg small">held — {i.hold_reason ?? 'no reason'}</div>}
            </td>
            <td>
              {fmtDate(i.due_date)}
              {i.days_overdue > 0 && i.status !== 'paid' && (
                <div className="neg small">{i.days_overdue} days over</div>
              )}
              {i.discount_available && (
                <div className="small" style={{ color: 'var(--gold)' }}>
                  discount to {fmtDate(i.discount_due_on!)}
                </div>
              )}
            </td>
            <td>
              <Status tone={tone(i.status)}>{label(i.status)}</Status>
            </td>
            <td>
              {i.approval_status === 'not_required' ? (
                <span className="muted small">—</span>
              ) : (
                <Status tone={tone(i.approval_status)}>{label(i.approval_status)}</Status>
              )}
            </td>
            <td className="num">{money(i.total_minor, currency)}</td>
            <td className="num">
              {Number(i.open_minor) === 0 ? (
                <span className="muted">—</span>
              ) : (
                money(i.open_minor, currency)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function InvoicesPage() {
  const { currency } = useSession();
  const [status, setStatus] = useState('unpaid');
  const [approval, setApproval] = useState('');

  const query = new URLSearchParams();
  if (status) query.set('status', status);
  if (approval) query.set('approval', approval);

  const { data, error, loading } = useApi<{ invoices: Invoice[] }>(
    `/v1/ap/invoices?${query.toString()}`,
    [status, approval],
  );

  const invoices = data?.invoices ?? [];
  const open = invoices.reduce((n, i) => n + Number(i.open_minor), 0);
  const waiting = invoices.filter((i) => i.approval_status === 'pending').length;

  return (
    <>
      <div className="row" style={{ marginBottom: 16, alignItems: 'baseline', gap: 12 }}>
        <h1>Invoices</h1>
        <span className="muted small">
          {invoices.length} · {money(open, currency)} open
        </span>
        <div className="spacer" />
        <Link to="/ap/invoices/new">
          <button>Enter an invoice</button>
        </Link>
      </div>

      <ErrorBanner error={error} />

      {waiting > 0 && (
        <div className="notice">
          <strong>
            {waiting} invoice{waiting === 1 ? '' : 's'} waiting for a signature.
          </strong>{' '}
          Nothing waiting for approval will be selected into a payment run.
        </div>
      )}

      <Card
        title="What the Club Owes"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 'auto' }}>
              <option value="unpaid">Unpaid</option>
              <option value="open">Open</option>
              <option value="partly_paid">Part paid</option>
              <option value="paid">Paid</option>
              <option value="draft">Draft</option>
              <option value="void">Void</option>
              <option value="">Everything</option>
            </select>
            <select
              value={approval}
              onChange={(e) => setApproval(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">Any approval</option>
              <option value="pending">Waiting</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="not_required">Not needed</option>
            </select>
          </div>
        }
      >
        {loading ? (
          <Loading />
        ) : invoices.length === 0 ? (
          <Empty>Nothing matches that.</Empty>
        ) : (
          <InvoiceTable invoices={invoices} currency={currency} />
        )}
      </Card>
    </>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency, atLeast } = useSession();
  const { data, error, loading, reload } = useApi<any>(`/v1/ap/invoices/${id}`);
  const [reason, setReason] = useState('');
  const [onBehalf, setOnBehalf] = useState('');

  const decide = useMutation(async (decision: 'approved' | 'rejected') =>
    api(`/v1/ap/invoices/${id}/approve`, {
      method: 'POST',
      body: {
        decision,
        reason: reason.trim() || undefined,
        approver_name: onBehalf.trim() || undefined,
        approver_role: onBehalf.trim() ? 'board' : undefined,
      },
    }),
  );
  const post = useMutation(async () => api(`/v1/ap/invoices/${id}/post`, { method: 'POST' }));
  const voidIt = useMutation(async (r: string) =>
    api(`/v1/ap/invoices/${id}/void`, { method: 'POST', body: { reason: r } }),
  );

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  const inv = data.invoice;
  const req = data.approval_required;

  return (
    <>
      <div className="row" style={{ marginBottom: 4, alignItems: 'baseline', gap: 10 }}>
        <h1>{inv.invoice_number}</h1>
        <Link to={`/ap/vendors/${inv.vendor_id}`} className="muted">
          {inv.vendor_name}
        </Link>
        <div className="spacer" />
        <Link to="/ap/invoices">
          <button className="secondary small">All invoices</button>
        </Link>
      </div>

      <ErrorBanner error={decide.error} />
      <ErrorBanner error={post.error} />
      <ErrorBanner error={voidIt.error} />

      {!data.coding.balanced && (
        <div className="notice">
          <strong>The coding does not add up.</strong> The lines come to{' '}
          {money(data.coding.line_total_minor, currency)} against a total of{' '}
          {money(inv.total_minor, currency)} — a difference of{' '}
          {money(Math.abs(data.coding.difference_minor), currency)}. This invoice cannot be posted
          until they agree.
        </div>
      )}

      <div className="tiles">
        <div className="tile">
          <div className="tile__label">Total</div>
          <div className="tile__value">{money(inv.total_minor, currency)}</div>
          {Number(inv.paid_minor) > 0 && (
            <div className="muted small">{money(inv.paid_minor, currency)} paid</div>
          )}
        </div>
        <div className="tile">
          <div className="tile__label">Due</div>
          <div className="tile__value" style={{ fontSize: 20 }}>
            {fmtDate(inv.due_date)}
          </div>
          <div className="muted small">invoiced {fmtDate(inv.invoice_date)}</div>
        </div>
        <div className="tile">
          <div className="tile__label">Status</div>
          <div className="tile__value" style={{ fontSize: 20 }}>
            {label(inv.status)}
          </div>
          {inv.journal_entry_id && <div className="muted small">posted to the ledger</div>}
        </div>
        <div className="tile">
          <div className="tile__label">Approval</div>
          <div className="tile__value" style={{ fontSize: 20 }}>
            {label(inv.approval_status)}
          </div>
          {req && (
            <div className="muted small">
              needs {req.approvals_required} × {label(req.approver_role)}
            </div>
          )}
        </div>
      </div>

      <Card title="How It Is Coded">
        <table className="data">
          <thead>
            <tr>
              <th>Account</th>
              <th>Department</th>
              <th>Description</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l: any) => (
              <tr key={l.id}>
                <td>
                  <span className="mono">{l.account_number}</span>{' '}
                  <span className="muted small">{l.account_name}</span>
                </td>
                <td className="muted small">
                  {l.department_code} {l.department_name}
                </td>
                <td>
                  {l.description}
                  {l.is_1099 && <span className="muted small"> · 1099</span>}
                </td>
                <td className="num">{money(l.amount_minor, currency)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3}>
                <strong>Coded</strong>
              </td>
              <td className="num">
                <strong className={data.coding.balanced ? undefined : 'neg'}>
                  {money(data.coding.line_total_minor, currency)}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      {data.approvals.length > 0 && (
        <Card title="Who Signed">
          <table className="data">
            <thead>
              <tr>
                <th>Who</th>
                <th>As</th>
                <th>Decision</th>
                <th>When</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {data.approvals.map((a: any, i: number) => (
                <tr key={i}>
                  <td>{a.approver_name}</td>
                  <td className="muted small">{label(a.approver_role)}</td>
                  <td>
                    <Status tone={tone(a.decision)}>{label(a.decision)}</Status>
                  </td>
                  <td className="muted small">{fmtDate(a.decided_at)}</td>
                  <td className="small">{a.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {inv.approval_status === 'pending' && atLeast('manager') && (
        <Card title="Approve It">
          <p className="muted small" style={{ marginTop: 0 }}>
            The person who entered an invoice may not approve it. That is not a setting.
          </p>
          <div className="grid-2">
            <label className="field">
              <span>On whose behalf, if not your own</span>
              <input
                value={onBehalf}
                onChange={(e) => setOnBehalf(e.target.value)}
                placeholder="e.g. a Board decision, minuted"
              />
              <div className="hint">
                Recording somebody else's signature does not satisfy the separation of duties on
                your own account.
              </div>
            </label>
            <label className="field">
              <span>Reason</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          </div>
          <div className="actions">
            <button disabled={decide.busy} onClick={() => void decide.run('approved').then(reload)}>
              Approve
            </button>
            <button
              className="secondary"
              disabled={decide.busy}
              onClick={() => void decide.run('rejected').then(reload)}
            >
              Reject
            </button>
          </div>
        </Card>
      )}

      {atLeast('controller') && (
        <Card title="The Ledger">
          {inv.status === 'draft' ? (
            <>
              <p style={{ marginTop: 0 }}>
                Posting this recognizes the expense and puts{' '}
                <strong>{money(inv.total_minor, currency)}</strong> into accounts payable.
              </p>
              <div className="actions">
                <button
                  disabled={!data.coding.balanced || post.busy}
                  onClick={() => void post.run().then(reload)}
                >
                  {post.busy ? 'Posting…' : 'Post it'}
                </button>
                {!data.coding.balanced && (
                  <span className="muted small">The coding has to add up first.</span>
                )}
              </div>
            </>
          ) : inv.status === 'void' ? (
            <p className="muted" style={{ marginTop: 0 }}>
              This invoice was voided. {inv.void_reason ?? ''}
            </p>
          ) : (
            <VoidInvoice
              disabled={Number(inv.paid_minor) > 0}
              busy={voidIt.busy}
              onVoid={(r) => void voidIt.run(r).then(reload)}
            />
          )}
        </Card>
      )}
    </>
  );
}

function VoidInvoice({
  disabled,
  busy,
  onVoid,
}: {
  disabled: boolean;
  busy: boolean;
  onVoid: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  if (disabled) {
    return (
      <p className="muted" style={{ marginTop: 0 }}>
        Something has been paid against this invoice, so it cannot be voided. Void the payment
        first.
      </p>
    );
  }
  return (
    <>
      <p className="muted small" style={{ marginTop: 0 }}>
        Voiding reverses the entry that posted it. The invoice stays on the record.
      </p>
      <div className="row" style={{ gap: 8 }}>
        <input
          placeholder="Why is it being voided?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button className="secondary" disabled={!reason.trim() || busy} onClick={() => onVoid(reason)}>
          Void it
        </button>
      </div>
    </>
  );
}

// =====================================================================
// Entering an invoice
// =====================================================================

interface DraftLine {
  description: string;
  amount: string;
  account_number: string;
  department_code: string;
  is_1099: boolean;
}

export function NewInvoicePage() {
  const { currency } = useSession();
  const vendors = useApi<{ vendors: Vendor[] }>('/v1/ap/vendors');
  const coa = useApi<{ accounts: any[] }>('/v1/coa');
  const structure = useApi<{ departments: any[] }>('/v1/structure');

  const [head, setHead] = useState({
    vendor_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    total: '',
    description: '',
  });
  const [lines, setLines] = useState<DraftLine[]>([
    { description: '', amount: '', account_number: '', department_code: '', is_1099: false },
  ]);
  const [done, setDone] = useState<any>(null);

  const create = useMutation(async (post: boolean) =>
    api<any>('/v1/ap/invoices', {
      method: 'POST',
      body: {
        vendor_id: head.vendor_id,
        invoice_number: head.invoice_number.trim(),
        invoice_date: head.invoice_date,
        due_date: head.due_date || undefined,
        total_minor: parseMoney(head.total, currency) ?? 0,
        description: head.description.trim() || undefined,
        post,
        lines: lines
          .filter((l) => l.description.trim() && l.amount.trim())
          .map((l) => ({
            description: l.description.trim(),
            amount_minor: parseMoney(l.amount, currency) ?? 0,
            account_number: l.account_number || undefined,
            department_code: l.department_code || undefined,
            is_1099: l.is_1099,
          })),
      },
    }),
  );

  const total = parseMoney(head.total, currency) ?? 0;
  const coded = lines.reduce((n, l) => n + (parseMoney(l.amount, currency) ?? 0), 0);
  const difference = total - coded;

  // Only accounts somebody can actually post an expense to. Showing the
  // control accounts would be offering a choice the ledger refuses.
  const expenseAccounts = useMemo(
    () =>
      (coa.data?.accounts ?? []).filter(
        (a: any) => a.is_postable && a.is_active && !a.control_account_for,
      ),
    [coa.data],
  );

  if (done) {
    return (
      <>
        <h1 style={{ marginBottom: 4 }}>Invoice entered</h1>
        <div className="ok-banner">
          <strong>{done.invoice.invoice_number}</strong> for{' '}
          {money(done.invoice.total_minor, currency)} was recorded
          {done.invoice.status === 'draft' ? ' as a draft' : ' and posted'}.
          {done.approval_required && (
            <div style={{ marginTop: 6 }}>
              It needs {done.approval_required.approvals_required} ×{' '}
              {label(done.approval_required.approver_role)} before it can be paid.
            </div>
          )}
          <div className="actions" style={{ marginTop: 12 }}>
            <Link to={`/ap/invoices/${done.invoice.id}`}>
              <button>Open it</button>
            </Link>
            <button
              className="secondary"
              onClick={() => {
                setDone(null);
                setHead((h) => ({ ...h, invoice_number: '', total: '', description: '' }));
                setLines([
                  { description: '', amount: '', account_number: '', department_code: '', is_1099: false },
                ]);
              }}
            >
              Enter another
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Enter an invoice</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Code it line by line to the department that incurred it — one linen invoice covers the
        dining room and the locker room, and the club needs to know which.
      </p>

      <ErrorBanner error={create.error} />

      <Card title="The Invoice">
        <div className="grid-2">
          <label className="field">
            <span>Vendor</span>
            <select
              value={head.vendor_id}
              onChange={(e) => setHead((h) => ({ ...h, vendor_id: e.target.value }))}
            >
              <option value="">Choose…</option>
              {(vendors.data?.vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Their invoice number</span>
            <input
              value={head.invoice_number}
              onChange={(e) => setHead((h) => ({ ...h, invoice_number: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Invoice date</span>
            <input
              type="date"
              value={head.invoice_date}
              onChange={(e) => setHead((h) => ({ ...h, invoice_date: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Due date</span>
            <input
              type="date"
              value={head.due_date}
              onChange={(e) => setHead((h) => ({ ...h, due_date: e.target.value }))}
            />
            <div className="hint">Left blank, it comes from the vendor's terms.</div>
          </label>
          <label className="field">
            <span>Total</span>
            <input
              value={head.total}
              onChange={(e) => setHead((h) => ({ ...h, total: e.target.value }))}
              placeholder="1,240.00"
            />
          </label>
          <label className="field">
            <span>Description</span>
            <input
              value={head.description}
              onChange={(e) => setHead((h) => ({ ...h, description: e.target.value }))}
            />
          </label>
        </div>
      </Card>

      <Card
        title="Coding"
        actions={
          <span className={difference === 0 ? 'muted small' : 'neg small'}>
            {difference === 0
              ? 'the lines add up'
              : `out by ${money(Math.abs(difference), currency)}`}
          </span>
        }
      >
        <table className="data">
          <thead>
            <tr>
              <th>Description</th>
              <th>Account</th>
              <th>Department</th>
              <th className="num">Amount</th>
              <th>1099</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <input
                    value={l.description}
                    onChange={(e) =>
                      setLines((ls) => ls.map((x, j) => (i === j ? { ...x, description: e.target.value } : x)))
                    }
                  />
                </td>
                <td>
                  <select
                    value={l.account_number}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (i === j ? { ...x, account_number: e.target.value } : x)),
                      )
                    }
                  >
                    <option value="">Choose…</option>
                    {expenseAccounts.map((a: any) => (
                      <option key={a.account_number} value={a.account_number}>
                        {a.account_number} {a.account_name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={l.department_code}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (i === j ? { ...x, department_code: e.target.value } : x)),
                      )
                    }
                  >
                    <option value="">Choose…</option>
                    {(structure.data?.departments ?? []).map((d: any) => (
                      <option key={d.code} value={d.code}>
                        {d.code} {d.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={l.amount}
                    onChange={(e) =>
                      setLines((ls) => ls.map((x, j) => (i === j ? { ...x, amount: e.target.value } : x)))
                    }
                    style={{ textAlign: 'right' }}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={l.is_1099}
                    onChange={(e) =>
                      setLines((ls) => ls.map((x, j) => (i === j ? { ...x, is_1099: e.target.checked } : x)))
                    }
                  />
                </td>
                <td>
                  {lines.length > 1 && (
                    <button
                      className="secondary small"
                      onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="actions" style={{ marginTop: 12 }}>
          <button
            className="secondary small"
            onClick={() =>
              setLines((ls) => [
                ...ls,
                { description: '', amount: '', account_number: '', department_code: '', is_1099: false },
              ])
            }
          >
            Another line
          </button>
        </div>
      </Card>

      <Card title="Save It">
        <div className="actions">
          <button
            disabled={!head.vendor_id || !head.invoice_number.trim() || difference !== 0 || create.busy}
            onClick={async () => {
              const r = await create.run(true);
              if (r) setDone(r);
            }}
          >
            {create.busy ? 'Saving…' : 'Enter and post'}
          </button>
          <button
            className="secondary"
            disabled={!head.vendor_id || !head.invoice_number.trim() || create.busy}
            onClick={async () => {
              const r = await create.run(false);
              if (r) setDone(r);
            }}
          >
            Save as a draft
          </button>
          {difference !== 0 && (
            <span className="muted small">
              The coding has to add up before it can be posted.
            </span>
          )}
        </div>
      </Card>
    </>
  );
}

// =====================================================================
// Payment runs
// =====================================================================

export function PaymentRunsPage() {
  const { currency } = useSession();
  const { data, error, loading, reload } = useApi<{ runs: Run[] }>('/v1/ap/runs');
  const cash = useApi<any>('/v1/ap/cash-requirements');
  const [dueBy, setDueBy] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });

  const open = useMutation(async () =>
    api<any>('/v1/ap/runs', { method: 'POST', body: { due_by: dueBy } }),
  );

  const buckets: Record<string, any> = {};
  for (const b of cash.data?.buckets ?? []) buckets[b.bucket] = b;

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Payment runs</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Select what is due, number the checks, print them, then post. Nothing reaches the bank
        account until the paper exists.
      </p>

      <ErrorBanner error={error} />
      <ErrorBanner error={open.error} />

      <div className="tiles">
        <Bucket label="Overdue" b={buckets.overdue} currency={currency} bad />
        <Bucket label="This week" b={buckets.this_week} currency={currency} />
        <Bucket label="Next week" b={buckets.next_week} currency={currency} />
        <Bucket label="This month" b={buckets.this_month} currency={currency} />
      </div>

      {(cash.data?.discounts_expiring ?? []).length > 0 && (
        <div className="notice">
          <strong>
            {cash.data.discounts_expiring.length} early-payment discount
            {cash.data.discounts_expiring.length === 1 ? '' : 's'} expiring.
          </strong>{' '}
          {cash.data.discounts_expiring
            .slice(0, 3)
            .map((d: any) => `${d.vendor_name} by ${fmtDate(d.discount_due_on)}`)
            .join('; ')}
          {cash.data.discounts_expiring.length > 3 && ' …'}
        </div>
      )}

      <Card title="Start a Run">
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>Pay everything due by</span>
            <input
              type="date"
              value={dueBy}
              onChange={(e) => setDueBy(e.target.value)}
              style={{ maxWidth: 190 }}
            />
          </label>
          <button
            disabled={open.busy}
            onClick={async () => {
              const r = await open.run();
              if (r) reload();
            }}
          >
            {open.busy ? 'Selecting…' : 'Select what is due'}
          </button>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Invoices on hold, vendors on hold and anything still waiting for a signature are left
          out.
        </div>
      </Card>

      <Card title="Runs">
        {loading ? (
          <Loading />
        ) : (data?.runs ?? []).length === 0 ? (
          <Empty>No payment runs yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Run</th>
                <th>Paying</th>
                <th>Account</th>
                <th>Status</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data?.runs ?? []).map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/ap/runs/${r.id}`}>{r.run_number}</Link>
                    <div className="muted small">{label(r.method)}</div>
                  </td>
                  <td>{fmtDate(r.payment_date)}</td>
                  <td className="muted small">
                    {r.bank_account_number ? `${r.bank_account_number} ${r.bank_account_name}` : '—'}
                  </td>
                  <td>
                    <Status tone={tone(r.status)}>{label(r.status)}</Status>
                  </td>
                  <td className="num">{r.total_minor ? money(r.total_minor, currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function Bucket({
  label: text,
  b,
  currency,
  bad,
}: {
  label: string;
  b: any;
  currency: any;
  bad?: boolean;
}) {
  const amount = Number(b?.amount_minor ?? 0);
  return (
    <div className="tile">
      <div className="tile__label">{text}</div>
      <div className={bad && amount > 0 ? 'tile__value neg' : 'tile__value'}>
        {money(amount, currency)}
      </div>
      <div className="muted small">{b?.invoices ?? 0} invoices</div>
    </div>
  );
}

export function PaymentRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency } = useSession();
  const { data, error, loading, reload } = useApi<any>(`/v1/ap/runs/${id}`);
  const [firstNumber, setFirstNumber] = useState('');

  const print = useMutation(async () =>
    api<any>(`/v1/ap/runs/${id}/print`, {
      method: 'POST',
      body: firstNumber ? { first_check_number: Number(firstNumber) } : {},
    }),
  );
  const post = useMutation(async () => api<any>(`/v1/ap/runs/${id}/post`, { method: 'POST' }));
  const cancel = useMutation(async () => api<any>(`/v1/ap/runs/${id}/cancel`, { method: 'POST' }));

  async function download(format: 'pdf' | 'printboss' | 'alignment') {
    const q =
      format === 'alignment'
        ? '?alignment=true'
        : format === 'printboss'
          ? '?format=printboss'
          : '';
    const res = await fetch(`/v1/ap/runs/${id}/checks${q}`, {
      headers: { authorization: `Bearer ${getToken()}` },
    });
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = format === 'printboss' ? 'checks.txt' : 'checks.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  const run = data.run;
  const payments = data.payments ?? [];
  const total = payments.reduce((n: number, p: any) => n + Number(p.amount_minor), 0);
  const numbered = payments.filter((p: any) => p.check_number).length;

  return (
    <>
      <div className="row" style={{ marginBottom: 4, alignItems: 'baseline', gap: 10 }}>
        <h1>{run.run_number}</h1>
        <Status tone={tone(run.status)}>{label(run.status)}</Status>
        <div className="spacer" />
        <Link to="/ap/runs">
          <button className="secondary small">All runs</button>
        </Link>
      </div>

      <ErrorBanner error={print.error} />
      <ErrorBanner error={post.error} />
      <ErrorBanner error={cancel.error} />

      <div className="tiles">
        <div className="tile">
          <div className="tile__label">Checks</div>
          <div className="tile__value">{payments.length}</div>
          <div className="muted small">{numbered} numbered</div>
        </div>
        <div className="tile">
          <div className="tile__label">Total</div>
          <div className="tile__value">{money(total, currency)}</div>
        </div>
        <div className="tile">
          <div className="tile__label">Paying</div>
          <div className="tile__value" style={{ fontSize: 20 }}>
            {fmtDate(run.payment_date)}
          </div>
        </div>
        <div className="tile">
          <div className="tile__label">From</div>
          <div className="tile__value" style={{ fontSize: 20 }}>
            {run.bank_account_number ?? '—'}
          </div>
          <div className="muted small">{run.bank_account_name ?? ''}</div>
        </div>
      </div>

      {/* One live step at a time, in the order the money moves. */}
      {run.status === 'draft' && (
        <Card title="Number and Print">
          <p style={{ marginTop: 0 }}>
            This assigns check numbers and marks the run printed.{' '}
            <strong>Nothing reaches the bank account yet</strong> — posting does that, after you
            have the stack in your hand.
          </p>
          <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>First check number</span>
              <input
                value={firstNumber}
                onChange={(e) => setFirstNumber(e.target.value)}
                placeholder={run.first_check_number ? String(run.first_check_number) : 'next in sequence'}
                style={{ maxWidth: 180 }}
              />
            </label>
            <button disabled={payments.length === 0 || print.busy} onClick={() => void print.run().then(reload)}>
              {print.busy ? 'Numbering…' : 'Number the checks'}
            </button>
          </div>
        </Card>
      )}

      {run.status === 'printed' && (
        <Card title="Print, Then Post">
          <div className="actions" style={{ marginBottom: 10 }}>
            <button onClick={() => void download('pdf')}>Print the checks</button>
            <button className="secondary" onClick={() => void download('printboss')}>
              PrintBoss file
            </button>
            <button className="secondary" onClick={() => void download('alignment')}>
              Alignment sheet
            </button>
          </div>
          <p className="muted small">
            Check the stack against the numbers below before posting. If one jammed, spoil it —
            that consumes the number without touching the ledger.
          </p>
          <div className="actions" style={{ marginTop: 10 }}>
            <button disabled={post.busy} onClick={() => void post.run().then(reload)}>
              {post.busy ? 'Posting…' : `Post ${payments.length} checks`}
            </button>
            <span className="muted small">
              This credits the bank account and clears {money(total, currency)} from payables.
            </span>
          </div>
        </Card>
      )}

      {(run.status === 'draft' || run.status === 'printed') && (
        <Card title="Or Abandon It">
          <div className="actions">
            <button className="secondary small" disabled={cancel.busy} onClick={() => void cancel.run().then(reload)}>
              Cancel this run
            </button>
            <span className="muted small">
              The invoices go back into the pool. Any numbers already assigned stay spent.
            </span>
          </div>
        </Card>
      )}

      <Card title="The Checks">
        {payments.length === 0 ? (
          <Empty>Nothing selected into this run.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Check</th>
                <th>Vendor</th>
                <th>Invoices</th>
                <th>Status</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p: any) => (
                <tr key={p.id}>
                  <td className="mono">{p.check_number ?? '—'}</td>
                  <td>{p.vendor_name}</td>
                  <td className="small muted">
                    {(p.invoices ?? []).map((i: any) => i.invoice_number).join(', ')}
                  </td>
                  <td>
                    <Status tone={tone(p.status)}>{label(p.status)}</Status>
                  </td>
                  <td className="num">{money(p.amount_minor, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

// =====================================================================
// Approval rules
// =====================================================================

export function ApprovalRulesPage() {
  const { currency } = useSession();
  const { data, error, loading, reload } = useApi<any>('/v1/ap/approval-rules');
  const [form, setForm] = useState({
    threshold: '',
    approver_role: 'controller',
    approvals_required: '1',
    effective_from: new Date().toISOString().slice(0, 10),
    note: '',
  });

  const add = useMutation(async () =>
    api('/v1/ap/approval-rules', {
      method: 'POST',
      body: {
        threshold_minor: parseMoney(form.threshold, currency) ?? 0,
        approver_role: form.approver_role,
        approvals_required: Number(form.approvals_required) || 1,
        effective_from: form.effective_from,
        note: form.note.trim() || undefined,
      },
    }),
  );

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Approval thresholds</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Who has to sign, and above what. A rule is dated, so an invoice is judged by the threshold
        that was in force when it was written rather than by today's.
      </p>

      <ErrorBanner error={error} />
      <ErrorBanner error={add.error} />

      <Card title="The Rules">
        {loading ? (
          <Loading />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th className="num">Above</th>
                <th>Needs</th>
                <th>How many</th>
                <th>In force</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rules ?? []).map((r: any) => (
                <tr key={r.id} className={r.in_force ? undefined : 'muted'}>
                  <td className="num">{money(r.threshold_minor, currency)}</td>
                  <td>{label(r.approver_role)}</td>
                  <td>{r.approvals_required}</td>
                  <td className="small">
                    {fmtDate(r.effective_from)}
                    {r.effective_to ? ` – ${fmtDate(r.effective_to)}` : ''}
                    {r.in_force && (
                      <span className="muted"> · now</span>
                    )}
                  </td>
                  <td className="small muted">{r.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="A New Threshold">
        <div className="grid-2">
          <label className="field">
            <span>Anything above</span>
            <input
              value={form.threshold}
              onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
              placeholder="5,000.00"
            />
          </label>
          <label className="field">
            <span>Needs the signature of</span>
            <select
              value={form.approver_role}
              onChange={(e) => setForm((f) => ({ ...f, approver_role: e.target.value }))}
            >
              <option value="controller">Controller</option>
              <option value="club_admin">Club administrator</option>
              <option value="board">The Board</option>
            </select>
          </label>
          <label className="field">
            <span>How many signatures</span>
            <input
              type="number"
              min={1}
              value={form.approvals_required}
              onChange={(e) => setForm((f) => ({ ...f, approvals_required: e.target.value }))}
              style={{ maxWidth: 100 }}
            />
          </label>
          <label className="field">
            <span>In force from</span>
            <input
              type="date"
              value={form.effective_from}
              onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
            />
          </label>
        </div>
        <label className="field">
          <span>Note</span>
          <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
        </label>
        <div className="actions">
          <button
            disabled={!form.threshold.trim() || add.busy}
            onClick={async () => {
              const r = await add.run();
              if (r) reload();
            }}
          >
            Add the threshold
          </button>
        </div>
      </Card>
    </>
  );
}

// =====================================================================
// The reports
// =====================================================================

type ReportTab = 'aging' | 'cash' | '1099' | 'register';

export function ApReportsPage() {
  const [tab, setTab] = useState<ReportTab>('aging');
  const TABS: { id: ReportTab; label: string }[] = [
    { id: 'aging', label: 'Aging' },
    { id: 'cash', label: 'Cash Requirements' },
    { id: '1099', label: '1099' },
    { id: 'register', label: 'Check Register' },
  ];

  return (
    <>
      <h1 style={{ marginBottom: 12 }}>Payables Reports</h1>
      <div className="actions" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? '' : 'secondary'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'aging' && <AgingReport />}
      {tab === 'cash' && <CashReport />}
      {tab === '1099' && <TenNinetyNine />}
      {tab === 'register' && <CheckRegister />}
    </>
  );
}

function AgingReport() {
  const { currency } = useSession();
  const { data, error, loading } = useApi<any>('/v1/ap/aging');
  const tie = useApi<any>('/v1/ap/reconciliation');

  if (loading) return <Loading />;
  return (
    <>
      <ErrorBanner error={error} />

      {/*
        Payables is a liability, so the control account carries a credit
        balance and both figures come back negative. A club owes a
        positive amount of money; showing it as minus twelve thousand
        reads as the vendors owing the club.
      */}
      {tie.data && (
        <div className={tie.data.ties ? 'ok-banner' : 'notice'}>
          {tie.data.ties ? (
            <>
              <strong>The subledger ties to the control account.</strong>{' '}
              {money(Math.abs(tie.data.subledger_minor), currency)} both ways.
            </>
          ) : (
            <>
              <strong>The subledger does not tie.</strong> Payables says{' '}
              {money(Math.abs(tie.data.subledger_minor), currency)} and the control account says{' '}
              {money(Math.abs(tie.data.control_account_minor), currency)} — a difference of{' '}
              {money(Math.abs(tie.data.difference_minor), currency)}. A month cannot be closed on
              this.
            </>
          )}
        </div>
      )}

      <Card title={`What is owed, by age — ${fmtDate(data?.as_of)}`}>
        <table className="data">
          <thead>
            <tr>
              <th>Vendor</th>
              <th className="num">Not due</th>
              <th className="num">1–30</th>
              <th className="num">31–60</th>
              <th className="num">61–90</th>
              <th className="num">90+</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {(data?.summary ?? []).map((r: any) => (
              <tr key={r.vendor_id}>
                <td>{r.vendor_name}</td>
                <td className="num">{money(r.not_yet_due_minor, currency)}</td>
                <td className="num">{money(r.days_1_30_minor, currency)}</td>
                <td className="num">{money(r.days_31_60_minor, currency)}</td>
                <td className="num">{money(r.days_61_90_minor, currency)}</td>
                <td className={Number(r.days_over_90_minor) > 0 ? 'num neg' : 'num'}>
                  {money(r.days_over_90_minor, currency)}
                </td>
                <td className="num">
                  <strong>{money(r.total_minor, currency)}</strong>
                </td>
              </tr>
            ))}
            {data?.totals && (
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                <td className="num">{money(data.totals.not_yet_due_minor ?? 0, currency)}</td>
                <td className="num">{money(data.totals.days_1_30_minor ?? 0, currency)}</td>
                <td className="num">{money(data.totals.days_31_60_minor ?? 0, currency)}</td>
                <td className="num">{money(data.totals.days_61_90_minor ?? 0, currency)}</td>
                <td className="num">{money(data.totals.days_over_90_minor ?? 0, currency)}</td>
                <td className="num">
                  <strong>{money(data.totals.total_minor ?? 0, currency)}</strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function CashReport() {
  const { currency } = useSession();
  const { data, error, loading } = useApi<any>('/v1/ap/cash-requirements');
  if (loading) return <Loading />;

  const order = ['overdue', 'this_week', 'next_week', 'this_month', 'later'];
  const names: Record<string, string> = {
    overdue: 'Already overdue',
    this_week: 'Within a week',
    next_week: 'The week after',
    this_month: 'Within the month',
    later: 'Later',
  };
  const byBucket: Record<string, any> = {};
  for (const b of data?.buckets ?? []) byBucket[b.bucket] = b;

  return (
    <>
      <ErrorBanner error={error} />
      <Card title="What Has to Be Found, and by When">
        <table className="data">
          <thead>
            <tr>
              <th>When</th>
              <th className="num">Invoices</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {order.map((k) => (
              <tr key={k}>
                <td className={k === 'overdue' ? 'neg' : undefined}>{names[k]}</td>
                <td className="num muted">{byBucket[k]?.invoices ?? 0}</td>
                <td className="num">{money(byBucket[k]?.amount_minor ?? 0, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="hint" style={{ marginTop: 10 }}>
          Invoices on hold and vendors on hold are left out — this is what the club will actually
          be asked for.
        </div>
      </Card>

      {(data?.discounts_expiring ?? []).length > 0 && (
        <Card title="Discounts About to Lapse">
          <table className="data">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Invoice</th>
                <th>By</th>
                <th className="num">Saving</th>
              </tr>
            </thead>
            <tbody>
              {data.discounts_expiring.map((d: any, i: number) => (
                <tr key={i}>
                  <td>{d.vendor_name}</td>
                  <td className="mono small">{d.invoice_number}</td>
                  <td>{fmtDate(d.discount_due_on)}</td>
                  <td className="num">{money(d.discount_minor, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

function TenNinetyNine() {
  const { currency } = useSession();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, error, loading } = useApi<any>(`/v1/ap/1099?year=${year}`, [year]);

  return (
    <>
      <ErrorBanner error={error} />
      {data?.warning && <div className="notice">{data.warning} Chase them before January.</div>}

      <Card
        title={`Vendors paid in ${year}`}
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="secondary small" onClick={() => setYear((y) => y - 1)}>
              ←
            </button>
            <button className="secondary small" onClick={() => setYear((y) => y + 1)}>
              →
            </button>
          </div>
        }
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="tile__label">Reportable</div>
                <div className="tile__value">{data?.reportable_count ?? 0}</div>
                <div className="muted small">
                  over {money(data?.threshold_minor ?? 0, currency)}
                </div>
              </div>
              <div className="tile">
                <div className="tile__label">Between them</div>
                <div className="tile__value">
                  {money(data?.reportable_total_minor ?? 0, currency)}
                </div>
              </div>
              <div className="tile">
                <div className="tile__label">Below the threshold</div>
                <div className="tile__value">{data?.below_threshold ?? 0}</div>
              </div>
              <div className="tile">
                <div className="tile__label">Missing a W-9</div>
                <div className={(data?.missing_w9 ?? []).length ? 'tile__value neg' : 'tile__value'}>
                  {(data?.missing_w9 ?? []).length}
                </div>
              </div>
            </div>

            <table className="data">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Tax ID</th>
                  <th>W-9</th>
                  <th>Form</th>
                  <th className="num">Paid</th>
                </tr>
              </thead>
              <tbody>
                {(data?.vendors ?? []).map((v: any) => (
                  <tr key={v.vendor_id} className={v.reportable ? undefined : 'muted'}>
                    <td>
                      {v.legal_name ?? v.vendor_name}
                      {v.legal_name && v.legal_name !== v.vendor_name && (
                        <div className="muted small">trading as {v.vendor_name}</div>
                      )}
                    </td>
                    <td className="mono small">{v.tax_id_last4 ? `••••${v.tax_id_last4}` : '—'}</td>
                    <td>
                      {v.w9_on_file ? (
                        <Status tone="good">on file</Status>
                      ) : v.reportable ? (
                        <Status tone="critical">missing</Status>
                      ) : (
                        <span className="muted small">—</span>
                      )}
                    </td>
                    <td className="small muted">
                      {v.form_type ?? ''} {v.box ? `box ${v.box}` : ''}
                    </td>
                    <td className="num">{money(v.paid_minor, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>
    </>
  );
}

function CheckRegister() {
  const { currency } = useSession();
  const { data, error, loading } = useApi<any>('/v1/ap/check-register');

  return (
    <>
      <ErrorBanner error={error} />
      {data?.warning && (
        <div className="notice">
          <strong>{data.warning}</strong> A gap in the sequence is what an auditor looks for; every
          number should be a check, a spoiled check or a void.
          <div className="mono small" style={{ marginTop: 6 }}>
            {(data.gaps ?? []).slice(0, 40).join(', ')}
            {(data.gaps ?? []).length > 40 && ' …'}
          </div>
        </div>
      )}

      <Card
        title="Every Check the Club Has Issued"
        actions={
          data?.first ? (
            <span className="muted small">
              {data.first} – {data.last}
            </span>
          ) : null
        }
      >
        {loading ? (
          <Loading />
        ) : (data?.checks ?? []).length === 0 ? (
          <Empty>No checks have been issued.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Number</th>
                <th>Date</th>
                <th>Vendor</th>
                <th>Run</th>
                <th>Status</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.checks.map((c: any, i: number) => (
                <tr key={i}>
                  <td className="mono">{c.check_number}</td>
                  <td>{fmtDate(c.payment_date)}</td>
                  <td>{c.vendor_name}</td>
                  <td className="muted small">{c.run_number ?? '—'}</td>
                  <td>
                    <Status tone={tone(c.status)}>{label(c.status)}</Status>
                    {c.void_reason && <div className="muted small">{c.void_reason}</div>}
                  </td>
                  <td className="num">{money(c.amount_minor, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
